import type { z } from 'zod'

import type { ApiRequest } from '~/types/ApiRequest'
import { requestGeneratedResource } from '~/services/api/requestGeneratedResource'
import type { ApiRequestMethod } from '~/types/ApiRequestMethod'

/** A fixed URL, or a path parsed from the operation's own arguments. */
type OperationRoute<Arguments extends readonly unknown[], PathSchema extends z.ZodObject> =
  | string
  | {
      readonly schema: PathSchema
      readonly parse: (...args: Arguments) => z.input<PathSchema>
      readonly buildUrl: (path: z.output<PathSchema>) => string
    }

/** A request input parsed by `schema` from the operation's own arguments, sent as the body or the query. */
interface OperationInput<
  Arguments extends readonly unknown[],
  InputSchema extends z.ZodType<object>
> {
  readonly channel: 'body' | 'query'
  readonly schema: InputSchema
  readonly select: (...args: Arguments) => z.input<InputSchema>
}

/** Builds an endpoint operation with an optional path and an optional parsed body or query, returning a parsed success body. */
export const createGeneratedResourceOperation =
  <
    Arguments extends readonly unknown[],
    SuccessSchema extends z.ZodType,
    PathSchema extends z.ZodObject = z.ZodObject,
    InputSchema extends z.ZodType<object> = z.ZodType<object>
  >(
    client: ApiRequest,
    route: OperationRoute<Arguments, PathSchema>,
    successSchema: SuccessSchema,
    method: ApiRequestMethod,
    input?: OperationInput<Arguments, InputSchema>
  ): ((...args: Arguments) => Promise<z.output<SuccessSchema>>) =>
  async (...args: Arguments) =>
    requestGeneratedResource(
      client,
      typeof route === 'string'
        ? route
        : { buildUrl: route.buildUrl, schema: route.schema, value: route.parse(...args) },
      successSchema,
      method,
      input === undefined
        ? undefined
        : { channel: input.channel, value: input.schema.parse(input.select(...args)) }
    )
